<template>
    <div
        v-bind="containerProps"
        class="grow p-1.25 relative z-50 custom-scroll-y"
        :class="{ 'custom-scroll-x': horizontalScroll }"
        id="listDropdownBodyId"
    >
        <div
            v-bind="wrapperProps"
            class="gap-y-3"
        >
            <template
                v-for="item in list"
                :key="item.index"
            >
                <div
                    class="relative flex items-center max-mob:rounded-0 rounded-1.25 min-w-full"
                    :class="[
                        wrapClass(item.data, item.index),
                        { 'w-max': horizontalScroll },
                        { 'cursor-pointer': !itemsReadonly }
                    ]"
                    @click="changeItem(returnObject ? item.data : item.data[itemValue])"
                >
                    <div
                        v-if="multiple"
                        class="w-4"
                    >
                        <CustomCheckBox
                            :checked="isSelected(item.data[itemValue])"
                            :disabled="itemsReadonly"
                        />
                    </div>

                    <div
                        v-if="isCountry"
                        class="pt-1.5"
                    >
                        <CountryFlag
                            class="rounded-0.5 normal-size__dropdown"
                            :country="item.data.code"
                        />
                    </div>

                    <div
                        class="flex items-center truncate whitespace-nowrap"
                        :class="{ 'justify-center': center }"
                    >
                        <div class="flex items-center gap-x-2">
                            <div
                                class="text-xs font-medium text-gray-370 dark:text-white truncate whitespace-nowrap select-none"
                                :class="isCountry && 'min-w-10.5'"
                                v-html="item.data[itemText]"
                            />
                        </div>

                    </div>

                    <div
                        class="ml-2 relative"
                        v-if="item.data.beta"
                    >
                        <div class="absolute left-0 -top-3">
                            <Beta />
                        </div>
                    </div>

                    <div
                        v-if="isMobView && isSelected(item.data[itemValue])"
                        class="ml-auto"
                        :class="[item.data.beta ? 'pl-9' : 'pl-2']"
                    >
                        <OkLongIcon class="text-gray-200" />
                    </div>
                </div>

                <div
                    v-if="showDividerArray[item.index]"
                    class="-mx-1.25 my-1 -bottom-0.5 bg-gray-100 dark:bg-blue-770 z-20 h-1px"
                ></div>
            </template>
        </div>
    </div>
</template>

<script setup>
import { watch, computed } from 'vue'
import { useVirtualList } from '@vueuse/core'

import CustomCheckBox from '@/components/inputs/CustomCheckBox.vue'
import OkLongIcon from '@/components/icons/OkLongIcon.vue'
import Beta from '@/components/beta/Beta.vue'

import { scrollToTopById } from '@/helpers/app-helper'

const props = defineProps({
    selectItems: {
        type: Array,
        default: () => [],
    },
    isCountry: {
        type: Boolean,
        default: false,
    },
    multiple: {
        type: Boolean,
        default: false,
    },
    itemValue: {
        type: String,
        default: 'id',
    },
    returnObject: {
        type: Boolean,
        default: false,
    },
    mini: {
        type: Boolean,
        default: false,
    },
    itemText: {
        type: String,
        default: 'name',
    },
    center: {
        type: Boolean,
        default: false,
    },
    isMobView: {
        type: Boolean,
        default: false,
    },
    horizontalScroll: {
        type: Boolean,
        default: false,
    },
    search: {
        type: String,
        default: '',
    },
    divideByKey: {
        type: String,
        default: '',
    },
    itemsReadonly: {
        type: Boolean,
        default: false,
    },
})

const emits = defineEmits(['clickItem', 'focusInput', 'closeMenu'])

const currentItemLocal = defineModel('currentItemLocal', { type: [String, Number, Object, Array], default: null })

const items = computed(() => props.selectItems)

const { list, containerProps, wrapperProps } = useVirtualList(
    items,
    {
        itemHeight: getItemHeight,
    },
)

const showDividerArray = computed(() => list.value.map((item) => showDivider(item)))

function showDivider({ data, index }) {
    const nextItem = list.value[index + 1]

    if (props.isMobView || !props.divideByKey || !data[props.divideByKey] || !nextItem) {
        return false
    }

    return !!data[props.divideByKey] && !nextItem.data[props.divideByKey]
}

function getItemHeight(index) {
    const data = items.value[index]
    const height = props.isMobView ? 47 : props.mini ? 30 : 40

    let elementsCount = 1

    if (data.hasOwnProperty('items') && data.isOpen) {
        elementsCount += data.items.length

        data.items.forEach((internalElement) => {
            if (internalElement.hasOwnProperty('items') && internalElement.isOpen) {
                elementsCount += internalElement.items.length
            }
        })
    }

    const elementsHeight = elementsCount * height
    const ifLastElementOnMobile = (items.value.length - 1 === index) && props.isMobView
    return ifLastElementOnMobile ? (elementsHeight + 200) : index ? elementsHeight : (elementsHeight - (showDividerArray.value.some((item) => !!item) ? 0 : 4))
}

function wrapClass(item, index) {
    if (props.isMobView) {
        return [
            'h-7.25 py-1.5 border-b border-white-500 dark:border-gray-760',
            props.isCountry ? 'px-0' : 'px-2.5 space-x-2',
        ]
    }

    return [
        'hover:bg-gray-80 dark:hover:bg-blue-770',
        { 'bg-gray-80 dark:bg-blue-770': isSelected(item[props.itemValue]) },
        props.mini ? index && 'mt-0.5' : index && 'mt-1',
        props.isCountry ? 'px-0' : 'px-2.5 space-x-2',
        props.isCountry
            ? 'py-0 h-9'
            : props.mini ? 'py-1.5' : 'py-2.5',
    ]
}

function isSelected(item) {
    if (props.multiple) {
        if (!Array.isArray(currentItemLocal.value)) {
            return false
        }

        return !!currentItemLocal.value.find((i) => (props.returnObject ? i[props.itemValue] : i) === item)
    }

    if (!currentItemLocal.value) {
        return false
    }

    return props.returnObject
        ? item === currentItemLocal.value[props.itemValue]
        : item === currentItemLocal.value
}

function focusInput() {
    emits('focusInput')
}

function closeMenu() {
    emits('closeMenu')
}

function changeItem(item) {
    if (props.loading || props.itemsReadonly) {
        return
    }

    emits('clickItem', item)

    if (!props.multiple) {
        currentItemLocal.value = item
        closeMenu()
        return
    }

    if (!currentItemLocal.value) {
        currentItemLocal.value = []
        focusInput()
        return
    }

    const itemId = props.returnObject ? item[props.itemValue] : item

    if (currentItemLocal.value.find((i) => (props.returnObject ? i[props.itemValue] : i) === itemId)) {
        currentItemLocal.value = currentItemLocal.value.filter((i) => (props.returnObject ? i[props.itemValue] : i) !== itemId)
        focusInput()
        return
    }

    currentItemLocal.value.push(item)
    focusInput()
}

watch(() => props.search, () => {
    scrollToTopById('#listDropdownBodyId')
})
</script>
